import { type ReactNode, useContext, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUCSuggestions } from '@atlassian/navigation-bff-data-provider';

import { ALLOWED_APPS_FOR_LOOM_PREFETCHING, LOOM_ATI } from '../../../constants';
import { MenuControlContext } from '../../../utils';
import { useLoomVideoConfig } from '../SuggestionItem/hooks/use-loom-video-config';
import { convertSourceProductToProductType } from '../SuggestionItem/LoomVideoItem';

export function PopupWrapper({ children }: { children: ReactNode }) {
	const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');
	const { product, suggestionConfig } = useContext(MenuControlContext);
	const isLoomPrefetchingEnabled = ALLOWED_APPS_FOR_LOOM_PREFETCHING.includes(product);

	const { locale } = useIntl();

	const { data, fetchUCSuggestions } = useUCSuggestions({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		orgId: suggestionConfig.enabled ? suggestionConfig.orgId : '',
		product,
		locale,
	});

	const hasLoomEnabled = data?.items.some((item) => item.ati === LOOM_ATI);

	const skipInitialisation =
		!isLoomPrefetchingEnabled || !crossAppCreationEnabled || !hasLoomEnabled;

	useLoomVideoConfig({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		productName: convertSourceProductToProductType(product),
		environment: (suggestionConfig.enabled && suggestionConfig?.environment) || 'prod',
		onItemClick: suggestionConfig.enabled ? suggestionConfig.onItemClick : undefined,
		skipInitialisation,
	});

	useEffect(
		function fetchUCSuggestionsForLoomEnabledApps() {
			if (crossAppCreationEnabled && suggestionConfig.enabled && isLoomPrefetchingEnabled) {
				fetchUCSuggestions();
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[crossAppCreationEnabled, suggestionConfig.enabled, locale, isLoomPrefetchingEnabled],
	);

	return children;
}
